import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "app/store";
import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";
import { actions } from "features/builder/slice";
import { PieceListContext } from "features/builder/pieceList";
import { PieceImageViewer, ScreenModalProvider } from "features/builder/elements";
import { MyMapsTab } from "./MyMapsTab";
import { TrashMapsTab } from "./TrashMapsTab";
import { PublicMapsTab } from "./PublicMapsTab";
import { GuideTargetId } from "./PieceBuilderTopGuideOverlay";

const TAB_ITEMS: {
  tab: PieceListContext;
  labelKey: string;
  activeBg: string;
  edgeBg: string;
  height?: number;
}[] = [
  {
    tab: PieceListContext.MINE,
    labelKey: "MSG_BUILDER_TAB_MY_MAPS",
    activeBg: "bg-orange-100",
    edgeBg: "bg-[#E1AD60]",
    // PUBLIC のテキスト上下余白に合わせて算出（PUBLIC 250 - 文字数差 2 × type-md 27px ≒ 196 → 200）
    height: 200,
  },
  {
    tab: PieceListContext.PUBLIC,
    labelKey: "MSG_BUILDER_TAB_PUBLIC_MAPS",
    activeBg: "bg-blue-100",
    edgeBg: "bg-[#9BBFD4]",
    // ラベルが長いため他タブより高めに確保
    height: 250,
  },
];

const TAB_BAR_WIDTH = 56;
// 右タブの縦並び（MINE 200 + PUBLIC 250 + gap-3 = 462）。TRASH は左下に独立ボタンとして配置
const TAB_HEIGHT = 160;

export const PieceBuilderTopPage = () => {
  const play = usePlaySound();
  const dispatch = useDispatch<AppDispatch>();
  const activeTab = useSelector((state: RootState) => state.builder.listUi.activeTab);
  const [isDetail, setIsDetail] = useState(false);
  // くわしくページの画像タップで全画面表示するためのビューア state。
  // ここ（root レベル）に持つことで、ゲーム機の枠全体を覆える。
  const [imageViewer, setImageViewer] = useState<{ src: string; alt: string } | null>(null);
  const openImage = (src: string, alt: string) => setImageViewer({ src, alt });
  const closeImage = () => setImageViewer(null);

  const handleSelectTab = (tab: PieceListContext) => () => {
    if (tab === activeTab) return;
    play();
    btnDelay(() => dispatch(actions.setActiveListTab(tab)));
  };

  return (
    <div className="flex-col-el relative w-full h-full overflow-hidden bg-background">
      <ScreenModalProvider>
        <div
          className="flex-col-el flex-1 w-full h-full relative"
          style={{ paddingRight: isDetail ? 0 : TAB_BAR_WIDTH }}
        >
          {activeTab === PieceListContext.MINE && (
            <MyMapsTab onOpenImage={openImage} onDetailChange={setIsDetail} />
          )}
          {activeTab === PieceListContext.PUBLIC && (
            <PublicMapsTab onOpenImage={openImage} onDetailChange={setIsDetail} />
          )}
          {activeTab === PieceListContext.TRASH && <TrashMapsTab onOpenImage={openImage} />}
        </div>

        {!isDetail && (
          <div
            className="flex-col-el justify-start items-end absolute right-0 top-0 h-full gap-3 pt-4 pointer-events-none"
            style={{ width: TAB_BAR_WIDTH }}
          >
            {TAB_ITEMS.map(({ tab, labelKey, activeBg, edgeBg, height }) => {
              const isActive = activeTab === tab;
              const tabHeight = height ?? TAB_HEIGHT;
              return (
                <div
                  key={tab}
                  id={
                    tab === PieceListContext.MINE
                      ? GuideTargetId.TAB_MINE
                      : GuideTargetId.TAB_PUBLIC
                  }
                  className="relative shrink-0"
                  style={{ width: TAB_BAR_WIDTH, height: tabHeight }}
                >
                  <div
                    className={`flex-col-el absolute top-0 right-0 w-full h-full rounded-tl-3xl rounded-bl-3xl ${
                      isActive ? edgeBg : "bg-gray2-60"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handleSelectTab(tab)}
                    style={{
                      width: TAB_BAR_WIDTH,
                      height: tabHeight - 8,
                    }}
                    className={`type-inherit-font flex-col-el shrink-0 items-center justify-center relative rounded-l-3xl py-4 cursor-pointer pointer-events-auto overflow-hidden border-0 ${
                      isActive ? `${activeBg} z-10` : "bg-gray2-100"
                    }`}
                  >
                    <p className="type-text type-md text-white text-center [writing-mode:vertical-rl]">
                      {I18n.t(labelKey)}
                    </p>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* ゴミばこは creator と同じ位置感（左下・パネル下）に独立配置 */}
        {!isDetail && (
          <button
            type="button"
            id={GuideTargetId.TRASH}
            onClick={handleSelectTab(PieceListContext.TRASH)}
            aria-label={I18n.t("MSG_BUILDER_TAB_TRASH")}
            className={`type-inherit-font flex-row-el flex-center absolute bottom-0 left-4 gap-2 px-6 h-14 rounded-t-2xl border-0 cursor-pointer pointer-events-auto z-20 ${
              activeTab === PieceListContext.TRASH ? "bg-brown-80" : "bg-gray2-100"
            }`}
          >
            <span className="material-symbols-outlined type-md text-white">delete</span>
            <p className="type-text type-body text-white whitespace-nowrap">
              {I18n.t("MSG_BUILDER_TAB_TRASH")}
            </p>
          </button>
        )}

        {imageViewer && (
          <PieceImageViewer src={imageViewer.src} alt={imageViewer.alt} close={closeImage} />
        )}
      </ScreenModalProvider>
    </div>
  );
};
